import axios from 'axios';
import { setGlobalState, clearCurrentStateInLocalstorage, getGlobalState } from '../state';
import authService from './authService';

// Optional: set baseURL for all requests
// axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL ?? '';

// URLs (or path prefixes) that should not receive Authorization header by default
const DEFAULT_NO_AUTH_PATHS = [
    '/api/Account',   // authentication endpoints en Caja.API
    '/public/',     // está por si en el futuro queremos mostrar páginas públicas sin auth  
    '/api/public'   // está por si en el futuro queremos mostrar páginas públicas sin auth
];

/**
 * Helper to determine if a request should skip attaching the auth token.
 * - If config.skipAuth === true
 * - Or header 'X-Skip-Auth' is present (truthy)
 * - Or the URL matches any prefix in DEFAULT_NO_AUTH_PATHS
 */
const shouldSkipAuthForRequest = (config: any) => {
  try {
    if (!config) return false;
    if (config.skipAuth === true) return true;
    const skipHeader = config.headers && (config.headers['X-Skip-Auth'] || config.headers['x-skip-auth']);
    if (skipHeader) return true;
    const url: string = config.url || '';
    // if url is absolute, try to extract path
    let path = url;
    try {
      if (url.startsWith('http')) {
        const u = new URL(url);
        path = u.pathname;
      }
    } catch (e) {
      // ignore
    }
    for (const prefix of DEFAULT_NO_AUTH_PATHS) {
      if (path.startsWith(prefix)) return true;
    }
  } catch (e) {
    // on error, don't skip
    console.error('Error checking skipAuth for request', e);
  }
  return false;
};

/**
 * Global request interceptor:
 * - Attach Authorization header from global state if available
 * - Skip attachment when requested or for configured paths
 */
axios.interceptors.request.use(
  async (config) => {
    try {
      if (shouldSkipAuthForRequest(config)) {
        // Ensure we don't forward any Authorization header if present
        if (config.headers) {
          delete config.headers.Authorization;
          delete config.headers.authorization;
        }
        return config;
      }

      const user = getGlobalState('user');
      if (user) {
        // accessToken may be a string or a Promise in some parts of the app
        let token: any = user.accessToken;
        if (token && typeof token.then === 'function') {
          token = await token;
        }
        if (token) {
          config.headers = {
            ...(config.headers || {}),
            Authorization: `Bearer ${token}`,
          };
        }
      }
    } catch (e) {
      // ignore and continue without auth header
      console.error('Error attaching auth token to request', e);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Global response interceptor:
 * - On 401: clear session, update global state and redirect to sign-in.
 */
axios.interceptors.response.use(
  response => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error.config;

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const isRemembered = localStorage.getItem("rememberedUser");
      if (isRemembered) {
        const user = getGlobalState('user');
        if (user && user.accessToken && user.refreshToken) {
          try {
            const response = await authService.refreshToken(user.accessToken, user.refreshToken);
            if (response && response.success) {
              const newUserData = { 
                  ...user, 
                  accessToken: response.data.accessToken, 
                  refreshToken: response.data.refreshToken 
              };
              setGlobalState('user', newUserData);
              
              // Update Authorization header for the failed request
              originalRequest.headers['Authorization'] = `Bearer ${response.data.accessToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            console.error('Error refreshing token', refreshError);
          }
        }
      }

      try {
        // Clear session storage and global state
        clearCurrentStateInLocalstorage();
        setGlobalState('user', undefined);
      } catch (e) {
        console.error('Error clearing global state on 401', e);
      }

      const signinPath = '/auth/signin';
      // Avoid redirect loop if already on sign-in
      if (window.location.pathname !== signinPath) {
        window.location.href = signinPath;
      }
    }

    return Promise.reject(error);
  }
);

export default axios;
